<template>
    <div>
        <ui-table-card>
            <template #toolbar>
                <div>
                    <div class="ui-section-title">Custódios da chave</div>
                    <div class="ui-section-description">Quem guarda um fragmento da chave da urna (R7). Entram no site de ativos com o e-mail e um código enviado a cada acesso, sem senha.</div>
                </div>
                <span class="ui-toolbar__spacer"></span>
                <v-btn v-if="$allowed('competitive.custody')" color="primary" depressed @click="openInvite"><v-icon left small>mdi-account-plus-outline</v-icon>Convidar custódio</v-btn>
            </template>
            <v-data-table :headers="headers" :items="items" :loading="loading" disable-sort disable-pagination hide-default-footer no-data-text="Nenhum custódio convidado" loading-text="Carregando...">
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ item.email }}<span v-if="item.organization"> · {{ item.organization }}</span></div>
                </template>
                <template v-slot:[`item.role_label`]="{ item }"><ui-badge :text="item.role_label" tone="neutral" :dot="false" /></template>
                <template v-slot:[`item.invited_at`]="{ item }">{{ formatStamp(item.invited_at) }}</template>
                <template v-slot:[`item.key_fingerprint`]="{ item }">
                    <template v-if="item.key_fingerprint">
                        <div class="ui-mono ui-small">{{ shortFingerprint(item.key_fingerprint) }}</div>
                        <div class="ui-cell-sub">Gerada em {{ formatStamp(item.key_registered_at) }}</div>
                    </template>
                    <ui-badge v-else text="Sem chave" tone="warning" />
                </template>
                <template v-slot:[`item.access`]="{ item }">
                    <ui-badge v-if="item.blocked" text="Bloqueado" tone="danger" />
                    <template v-else-if="item.first_access_at">
                        <ui-badge text="Acessou" tone="success" />
                        <div class="ui-cell-sub">Último acesso {{ formatStamp(item.last_login_at) }}</div>
                    </template>
                    <ui-badge v-else text="Aguardando o primeiro acesso" tone="warning" />
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Reenviar convite', icon: 'mdi-email-sync-outline', handler: () => resend(item), disabled: !$allowed('competitive.custody') },
                        { text: 'Remover', icon: 'mdi-account-remove-outline', handler: () => remove(item), danger: true, disabled: !$allowed('competitive.custody') },
                    ]" />
                </template>
            </v-data-table>
        </ui-table-card>

        <ui-section title="Custódios necessários para abrir a urna" description="Quantos fragmentos a sessão pública precisa juntar (R7). Fica fixo depois do primeiro lacre." class="mt-4">
            <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                <v-select v-model="threshold" :items="thresholdOptions" outlined dense hide-details style="max-width: 220px;" :disabled="sealedAny || !$allowed('competitive.custody')" :menu-props="{ offsetY: true }"></v-select>
                <span class="ui-small">de {{ items.length }} custódio(s)</span>
                <v-btn v-if="!sealedAny && $allowed('competitive.custody')" small outlined :loading="savingThreshold" :disabled="threshold === savedThreshold" @click="saveThreshold">Salvar</v-btn>
                <span v-if="sealedAny" class="ui-small ui-muted">Já há urna lacrada.</span>
            </div>
        </ui-section>

        <ui-section title="Urnas das etapas" description="O lacre é feito pelo custódio da AJ na área dos custódios do site de ativos. Nenhuma chave passa pelo back-office (D2)." class="mt-4">
            <div v-if="!urns.length" class="ui-small ui-muted">Nenhuma etapa cadastrada.</div>
            <div v-for="stage in urns" :key="stage.stage_id" class="mb-4">
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <strong>Etapa {{ stage.number }} · {{ stage.name }}</strong>
                    <ui-badge v-if="stage.status === 'cancelled'" text="Cancelada" tone="neutral" />
                    <ui-badge v-else-if="stage.urn && stage.urn.closed_at" text="Urna fechada" tone="neutral" />
                    <ui-badge v-else-if="stage.urn" :text="`Lacrada em ${formatStamp(stage.urn.sealed_at)}`" tone="success" />
                    <ui-badge v-else text="Não lacrada" tone="warning" />
                </div>
                <template v-if="stage.urn">
                    <div class="ui-small mt-1">
                        Impressão digital <span class="ui-mono">{{ shortFingerprint(stage.urn.fingerprint) }}</span> ·
                        {{ stage.urn.threshold }} de {{ stage.urn.total_shares }} abrem · lacrada por {{ stage.urn.sealed_by || "—" }}
                    </div>
                    <div class="ui-small mt-1">
                        <template v-if="stage.urn.closed_at">
                            Fechada em {{ formatStamp(stage.urn.closed_at) }} com {{ stage.urn.envelope_count }} envelope(s) no conjunto ·
                            código do conjunto <span class="ui-mono">{{ stage.urn.set_hash }}</span>
                        </template>
                        <template v-else>
                            {{ stage.urn.envelopes_received }} envelope(s) recebido(s), {{ stage.urn.envelopes_active }} vigente(s). O conteúdo só é lido na abertura da urna.
                        </template>
                    </div>
                    <div v-if="stage.urn.ceremony_notes" class="ui-small ui-muted mt-1">{{ stage.urn.ceremony_notes }}</div>
                    <v-simple-table dense class="mt-2">
                        <thead><tr><th>Fragmento</th><th>Custódio</th><th>Chave pessoal</th><th>Retirada</th></tr></thead>
                        <tbody>
                            <tr v-for="share in stage.urn.shares" :key="share.share_index">
                                <td>{{ share.share_index }}</td>
                                <td>{{ share.custodian_name }}<span class="ui-muted"> · {{ share.role_label }}</span></td>
                                <td class="ui-mono ui-small">{{ shortFingerprint(share.recipient_fingerprint) }}</td>
                                <td>
                                    <ui-badge v-if="share.withdrawn_at" :text="formatStamp(share.withdrawn_at)" tone="success" />
                                    <ui-badge v-else text="Não retirada" tone="warning" />
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </template>
            </div>
        </ui-section>

        <ui-section title="Como funciona" class="mt-4">
            <p class="ui-small mb-2">Cada custódio gera a própria chave pessoal na área dos custódios e guarda o arquivo com uma senha que só ele conhece. Na cerimônia de lacre, o navegador do custódio da AJ gera a chave da urna e a divide em fragmentos, cada um cifrado para a chave pessoal de um custódio. Depois, cada custódio retira e confere o próprio fragmento.</p>
            <p class="ui-small ui-muted mb-0">A plataforma nunca recebe a chave da urna, as chaves pessoais nem os fragmentos abertos. Convide pessoas diferentes da AJ, do juízo e do MP.</p>
        </ui-section>

        <v-dialog v-model="invite.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">Convidar custódio</h3></div>
                <div class="ui-card__body">
                    <v-text-field v-model="invite.form.name" label="Nome completo" outlined dense hide-details="auto" class="mb-4" maxlength="255"></v-text-field>
                    <v-text-field v-model="invite.form.email" label="E-mail" type="email" outlined dense hide-details="auto" class="mb-4" maxlength="255"></v-text-field>
                    <v-row dense>
                        <v-col cols="12" sm="6"><v-select v-model="invite.form.role" :items="options.custodian_roles || []" label="Representa" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="6"><v-text-field v-model="invite.form.cpf" label="CPF (opcional)" outlined dense hide-details="auto" maxlength="14"></v-text-field></v-col>
                    </v-row>
                    <v-text-field v-model="invite.form.organization" label="Órgão ou cargo (opcional)" outlined dense hide-details="auto" class="mt-4" maxlength="255" placeholder="Ex.: Promotoria de Justiça de Cruz Alta"></v-text-field>
                    <p class="ui-small ui-muted mt-3 mb-0">A pessoa recebe um e-mail com o link da área dos custódios.</p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="invite.saving" @click="invite.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="invite.saving" :disabled="!canInvite" @click="send">Enviar convite</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { API, formatStamp, notifyError, notifySuccess } from "@/components/competitive/competitive.util";

export default {
    name: "CompetitiveCustodians",
    props: {
        processId: { type: Number, required: true },
        options: { type: Object, required: true },
    },
    data: () => ({
        items: [],
        urns: [],
        threshold: 2,
        savedThreshold: 2,
        savingThreshold: false,
        loading: false,
        invite: { open: false, saving: false, form: {} },
        headers: [
            { text: "Custódio", value: "name" },
            { text: "Representa", value: "role_label", width: 200 },
            { text: "Convidado em", value: "invited_at", width: 160 },
            { text: "Chave pessoal", value: "key_fingerprint", width: 210 },
            { text: "Acesso", value: "access", width: 230 },
            { text: "", value: "actions", align: "end", width: 48 },
        ],
    }),
    computed: {
        sealedAny() {
            return this.urns.some((stage) => stage.urn);
        },
        thresholdOptions() {
            const max = Math.max(this.items.length, this.threshold, 2);
            return Array.from({ length: max - 1 }, (_, index) => ({ value: index + 2, text: `${index + 2} custódios` }));
        },
        canInvite() {
            const form = this.invite.form;
            return !!String(form.name || "").trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || "").trim()) && !!form.role;
        },
    },
    watch: {
        processId: {
            immediate: true,
            handler() {
                this.load();
            },
        },
    },
    methods: {
        formatStamp,
        shortFingerprint(hash) {
            return String(hash || "").slice(0, 16).toUpperCase().replace(/(.{4})(?=.)/g, "$1 ");
        },
        async load() {
            this.loading = true;
            try {
                const [custodians, urns] = await Promise.all([
                    axios.get(`${API}/processes/${this.processId}/custodians`),
                    axios.get(`${API}/processes/${this.processId}/urns`),
                ]);
                this.items = custodians.data.content;
                this.urns = urns.data.content.stages;
                this.threshold = this.savedThreshold = urns.data.content.threshold;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        openInvite() {
            // Sugere o papel que ainda falta (AJ, juizo, MP).
            const taken = new Set(this.items.map((item) => item.role));
            const role = ["aj", "court", "mp"].find((value) => !taken.has(value)) || "other";
            this.invite = { open: true, saving: false, form: { name: "", email: "", cpf: "", organization: "", role } };
        },
        async send() {
            this.invite.saving = true;
            try {
                await axios.post(`${API}/processes/${this.processId}/custodians`, this.invite.form);
                notifySuccess(this, "Convite enviado.");
                this.invite.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.invite.saving = false;
            }
        },
        async saveThreshold() {
            this.savingThreshold = true;
            try {
                await axios.patch(`${API}/processes/${this.processId}`, { custody_threshold: this.threshold });
                this.savedThreshold = this.threshold;
                notifySuccess(this, "Número de custódios necessários salvo.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.savingThreshold = false;
            }
        },
        async resend(item) {
            try {
                await axios.post(`${API}/custodians/${item.id}/resend-invite`);
                notifySuccess(this, `Convite reenviado para ${item.email}.`);
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
        async remove(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Remover custódio",
                text: `${item.name} deixa de ser custódio deste processo e perde o acesso à área dos custódios dele. A remoção fica na trilha. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Remover", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/custodians/${item.id}`);
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>
